import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MatchIdentifierService } from '../services/match-identifier-service';
import httpClient from '../services/http-client';
import { 
  MatchIdentifier,
  MatchIdentifiersListResponse,
  CreateMatchIdentifierRequest,
  UpdateMatchIdentifierRequest,
  TestMatchIdentifierRequest,
  TestMatchIdentifierResult,
  MatchIdentifierTypesResponse
} from '../types';

// Create a match identifier service instance using the default HTTP client
const matchIdentifierService = new MatchIdentifierService(httpClient);

// Query keys
export const matchIdentifierQueryKeys = {
  all: ['matchIdentifiers'] as const,
  divisions: () => [...matchIdentifierQueryKeys.all, 'divisions'] as const,
  division: (divisionId: string) => [...matchIdentifierQueryKeys.divisions(), divisionId] as const,
  lists: (divisionId: string) => [...matchIdentifierQueryKeys.division(divisionId), 'list'] as const,
  list: (divisionId: string, filters: Record<string, any>) => [...matchIdentifierQueryKeys.lists(divisionId), filters] as const,
  details: (divisionId: string) => [...matchIdentifierQueryKeys.division(divisionId), 'detail'] as const,
  detail: (divisionId: string, id: string) => [...matchIdentifierQueryKeys.details(divisionId), id] as const,
  types: (divisionId: string) => [...matchIdentifierQueryKeys.division(divisionId), 'types'] as const,
};

/**
 * Hook to get all match identifiers for a division with optional filtering
 */
export function useMatchIdentifiers(
  divisionId: string,
  params?: {
    active?: boolean;
    search?: string;
    type?: string;
  },
  enabled = true
) {
  return useQuery({
    queryKey: matchIdentifierQueryKeys.list(divisionId, params || {}),
    queryFn: () => matchIdentifierService.getMatchIdentifiers(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get a specific match identifier by ID
 */
export function useMatchIdentifier(
  divisionId: string, 
  identifierId: string, 
  enabled = true
) {
  return useQuery({
    queryKey: matchIdentifierQueryKeys.detail(divisionId, identifierId),
    queryFn: () => matchIdentifierService.getMatchIdentifier(divisionId, identifierId),
    enabled: enabled && !!divisionId && !!identifierId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get available match identifier types and operators
 */
export function useMatchIdentifierTypes(divisionId: string, enabled = true) {
  return useQuery({
    queryKey: matchIdentifierQueryKeys.types(divisionId),
    queryFn: () => matchIdentifierService.getMatchIdentifierTypes(divisionId),
    enabled: enabled && !!divisionId,
    staleTime: 30 * 60 * 1000, // 30 minutes - types don't change often
  });
}

/**
 * Hook to create a new match identifier
 */
export function useCreateMatchIdentifier() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, request }: { 
      divisionId: string; 
      request: CreateMatchIdentifierRequest;
    }) => matchIdentifierService.createMatchIdentifier(divisionId, request),
    onSuccess: (_, { divisionId }) => {
      // Invalidate and refetch match identifiers list for this division
      queryClient.invalidateQueries({ 
        queryKey: matchIdentifierQueryKeys.division(divisionId) 
      });
    },
  });
}

/**
 * Hook to update an existing match identifier
 */
export function useUpdateMatchIdentifier() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, identifierId, request }: { 
      divisionId: string;
      identifierId: string; 
      request: UpdateMatchIdentifierRequest;
    }) => matchIdentifierService.updateMatchIdentifier(divisionId, identifierId, request),
    onSuccess: (_, { divisionId, identifierId }) => {
      // Invalidate and refetch match identifiers for this division
      queryClient.invalidateQueries({ 
        queryKey: matchIdentifierQueryKeys.division(divisionId) 
      });
      // Also invalidate the specific match identifier detail
      queryClient.invalidateQueries({ 
        queryKey: matchIdentifierQueryKeys.detail(divisionId, identifierId) 
      });
    },
  });
}

/**
 * Hook to deactivate a match identifier (soft delete)
 */
export function useDeactivateMatchIdentifier() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ divisionId, identifierId }: { 
      divisionId: string;
      identifierId: string;
    }) => matchIdentifierService.deactivateMatchIdentifier(divisionId, identifierId),
    onSuccess: (_, { divisionId }) => {
      // Invalidate and refetch match identifiers list for this division
      queryClient.invalidateQueries({ 
        queryKey: matchIdentifierQueryKeys.division(divisionId) 
      });
    },
  });
}

/**
 * Hook to test a match identifier against sample data
 */
export function useTestMatchIdentifier() {
  return useMutation({
    mutationFn: ({ divisionId, request }: { 
      divisionId: string; 
      request: TestMatchIdentifierRequest;
    }) => matchIdentifierService.testMatchIdentifier(divisionId, request),
  });
}
